import { useState, useEffect, useCallback } from 'react'

/**
 * Hook to track online/offline status
 * Returns current online state and provides manual check capability
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(() => {
    // Handle SSR and initial state
    if (typeof navigator !== 'undefined') {
      return navigator.onLine
    }
    return true
  })

  const [lastOnline, setLastOnline] = useState<Date | null>(null)

  // Update state when online/offline events fire
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      setLastOnline(new Date())
    }

    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Manual connectivity check
  const checkConnection = useCallback(async (): Promise<boolean> => {
    try {
      // Try to fetch a simple resource
      const response = await fetch('/favicon.svg', {
        method: 'HEAD',
        cache: 'no-store',
      })
      const online = response.ok
      setIsOnline(online)
      if (online) {
        setLastOnline(new Date())
      }
      return online
    } catch {
      setIsOnline(false)
      return false
    }
  }, [])

  return {
    isOnline,
    lastOnline,
    checkConnection,
  }
}

/**
 * Simple hook that just returns online status
 */
export function useIsOnline(): boolean {
  const { isOnline } = useOnlineStatus()
  return isOnline
}
